import axios from "axios";
import {zurichZipCodes} from "../columns/violationsColumns";
import {apiService, rejectIfNotOk} from "../utils/serviceApi";
import {
  createDocumentErrorNotification,
  createSimpleAxiosErrorNotification,
} from "../common/notifications/notification";
import {downloadFile, PINNED_FILE_NAME} from "../utils/downloadFile";
import {
  ARCHIVED_TICKETS,
  CANCEL_TICKET,
  CANCEL_TICKET_FEE,
  CHANGE_TICKET_DOCUMENT_LANGUAGE,
  CHANGE_TICKET_LEGAL_STATUS,
  CLEAR_OWNERS_LIST_FOR_GS_AND_PZ_EDIT,
  CLEAR_TICKET_FOR_EDIT,
  CLOSE_TICKET,
  COMMENT_TICKET,
  DELETE_TICKET,
  GENERATE_DOCUMENTATION,
  GET_ALL_VIOLATIONS,
  GET_TICKET_FOR_EDIT,
  MANUALLY_PAY,
  MODIFY_TICKET,
  OWNERS_GAS_STATION_ZONES_FOR_EDITING_TICKET,
  OWNERS_PARKING_ZONES_FOR_EDITING_TICKET,
  REFUND_TICKET,
  RESET_TICKET,
  SET_LOADER,
  TICKET_LIST_FAILED,
  loaderKind,
} from "./types";

export const getAllViolations =
  (showArchivedTickets, setArchivedCellStyle, filter, ourRequest, country) =>
  async (dispatch) => {
    dispatch({
      type: SET_LOADER,
      payload: true,
      meta: { loader: loaderKind.GRID },
    });

    apiService
      .postWithCancelToken(
        `${
          process.env.REACT_APP_SERVICE_URL
        }/api/report/getTickets?filter=${encodeURI(JSON.stringify(filter))}`,
        { country },
        ourRequest
      )
      .then(rejectIfNotOk)
      .then((res) => {
        dispatch({
          type: GET_ALL_VIOLATIONS,
          payload: res?.data?.content,
          last: res?.data?.last,
          numberOfElements: res?.data?.numberOfElements,
          empty: res?.data?.empty
        });
      })
      .then(() => {
        setArchivedCellStyle(showArchivedTickets);
      })
      .catch((e) => {
        if (axios.isCancel(e)) {
          return;
        }
        createSimpleAxiosErrorNotification(e);
        dispatch({ type: TICKET_LIST_FAILED });
      })
      .finally(() => {
        dispatch({
          type: SET_LOADER,
          payload: false,
          meta: { loader: loaderKind.GRID },
        });
      });
  };

export const getAllDriveOffs =
  (showArchivedTickets, setArchivedCellStyle, filter, ourRequest, country) =>
  async (dispatch) => {
    dispatch({
      type: SET_LOADER,
      payload: true,
      meta: { loader: loaderKind.GRID },
    });
    apiService
      .postWithCancelToken(
        `${
          process.env.REACT_APP_SERVICE_URL
        }/api/report/getTickets?filter=${encodeURI(JSON.stringify(filter))}`,
        { country },
        ourRequest,
      )
      .then(rejectIfNotOk)
      .then((res) => {
        dispatch({
          type: GET_ALL_VIOLATIONS,
          payload: res?.data?.content,
          last: res?.data?.last,
          numberOfElements: res?.data?.numberOfElements,
          empty: res?.data?.empty
        });
      })
      .then(() => {
        setArchivedCellStyle(showArchivedTickets);
      })
      .catch((e) => {
        if (axios.isCancel(e)) {
          return;
        }
        createSimpleAxiosErrorNotification(e);
        dispatch({ type: TICKET_LIST_FAILED });
      })
      .finally(() => {
        dispatch({
          type: SET_LOADER,
          payload: false,
          meta: { loader: loaderKind.GRID },
        });
      });
  };

export const deleteTicket = (id) => async (dispatch) => {
  return apiService
    .delete(`${process.env.REACT_APP_SERVICE_URL}/api/report/archive/${id}`)
    .then(rejectIfNotOk)
    .then(() => {
      dispatch({
        type: DELETE_TICKET,
        payload: id,
      });
    })
    .catch((e) => {
      createSimpleAxiosErrorNotification(e);
      throw e;
    });
};

export const downloadZipDocumentation = async (id) => {
  return apiService
    .getDocument(`${process.env.REACT_APP_SERVICE_URL}/api/report/zip/${id}`)
    .then((response) => {
      return downloadFile(response, "multiCompressed.zip", PINNED_FILE_NAME);
    })
    .catch((e) => {
      createDocumentErrorNotification(e);
    });
};

/** Ticket audit history rows, or null (after an error notification) when the request fails. */
export const getAuditTickets = (referenceNumber) =>
  apiService
    .get(`${process.env.REACT_APP_SERVICE_URL}/api/report/audit/${referenceNumber}`)
    .then(rejectIfNotOk)
    .then((res) => res.data)
    .catch((e) => {
      createSimpleAxiosErrorNotification(e);
      return null;
    });

/**
 * BEP-1821: fetch the ticket-scoped holder lookup trail.
 * Returned directly as a resolved promise (local component state, not redux)
 * to avoid stale cross-ticket data.
 */
export const getHolderLookupTrail = (referenceNumber) =>
  apiService
    .get(`${process.env.REACT_APP_SERVICE_URL}/api/report/holderTrail/${referenceNumber}`)
    .then((res) => res.data)
    .catch((e) => {
      createSimpleAxiosErrorNotification(e);
      return null;
    });

/** Customer document history rows, or null (after an error notification) when the request fails. */
export const getCustomerDocumentHistory = (referenceNumber) =>
  apiService
    .get(`${process.env.REACT_APP_SERVICE_URL}/api/customerDocument?ticketNumber=${referenceNumber}`)
    .then(rejectIfNotOk)
    .then((res) => res.data)
    .catch((e) => {
      createSimpleAxiosErrorNotification(e);
      return null;
    });

/** Returns the updated customer document; rejects after an error notification. */
export const changeCustomerDocumentStatus = (id, status) =>
  apiService
    .put(`${process.env.REACT_APP_SERVICE_URL}/api/customerDocument/${id}/status`, { status })
    .then(rejectIfNotOk)
    .then((res) => res.data)
    .catch((e) => {
      createSimpleAxiosErrorNotification(e);
      throw e;
    });

export const downloadCustomerDocument =
  (ticketNumber, fileName) => async (dispatch) => {
    dispatch({
      type: SET_LOADER,
      payload: true,
    });
    return apiService
      .getDocument(
        `${process.env.REACT_APP_SERVICE_URL}/api/customerDocument/download?ticketNumber=${ticketNumber}&fileName=${fileName}`
      )
      .then((response) => {
        return downloadFile(response, fileName);
      })
      .catch((e) => {
        createDocumentErrorNotification(e);
      })
      .finally(() => {
        dispatch({
          type: SET_LOADER,
          payload: false,
        });
      });
  };

export const generateSueDocumentation =
  (ticketId, licencePlate, postalCode) => async (dispatch) => {
    dispatch({
      type: SET_LOADER,
      payload: true,
    });
    return apiService
      .getDocument(
        `${process.env.REACT_APP_SERVICE_URL}/api/report/${ticketId}/${licencePlate}/generateSueDocs`
      )
      .then(async (response) => {
        // only mark the ticket as documented once the document really arrived
        if (await downloadFile(response, "documentation.zip")) {
          dispatch({
            type: GENERATE_DOCUMENTATION,
            payload: ticketId,
          });
        }
      })
      .catch((e) => {
        createDocumentErrorNotification(e);
      })
      .finally(() => {
        dispatch({
          type: SET_LOADER,
          payload: false,
        });
      });
  };

export const createOperationalDoc =
  (ticketNumber, licencePlate) => async (dispatch) => {
    dispatch({
      type: SET_LOADER,
      payload: true,
    });
    return apiService
      .getDocument(
        `${process.env.REACT_APP_SERVICE_URL}/api/report/${ticketNumber}/${licencePlate}/printoutOperationalDocument`
      )
      .then((response) => {
        return downloadFile(response);
      })
      .catch((e) => {
        createDocumentErrorNotification(e);
      })
      .finally(() => {
        dispatch({
          type: SET_LOADER,
          payload: false,
        });
      });
  };

export const generatePaymentSlipReminder =
  (ticketNumber, licencePlate, selectedLanguage, sendViaPingen) => async (dispatch) => {
    // no page level loader here: ModalSendViaPingen shows its own spinner in
    // place of its body while the document is being generated
    return apiService
      .getDocument(
        `${process.env.REACT_APP_SERVICE_URL}/api/tickets/${ticketNumber}/${licencePlate}/${selectedLanguage}/printoutReminder?sendViaPingen=${sendViaPingen}`
      )
      .then((response) => {
        return downloadFile(response);
        //this is waiting for automatic changing legal status
        // dispatch({
        //   type: GENERATE_REMINDER_PAYMENT_SLIP,
        //   payload: ticketNumber,
        // });
      })
      .catch((e) => {
        createDocumentErrorNotification(e);
      });
  };

export const generateLastReminder =
  (ticketNumber, licencePlate, selectedLanguage, sendViaPingen) => async (dispatch) => {
    // no page level loader here: ModalSendViaPingen shows its own spinner in
    // place of its body while the document is being generated
    return apiService
      .getDocument(
        `${process.env.REACT_APP_SERVICE_URL}/api/tickets/${ticketNumber}/${licencePlate}/${selectedLanguage}/printoutLastReminder?sendViaPingen=${sendViaPingen}`
      )
      .then((response) => {
        return downloadFile(response);
      })
      .catch((e) => {
        createDocumentErrorNotification(e);
      });
  };

export const generatePaymentSlipWithPhotos =
  (ticketNumber, licencePlate, selectedLanguage, sendViaPingen) => async (dispatch) => {
    // no page level loader here: ModalSendViaPingen shows its own spinner in
    // place of its body while the document is being generated
    return apiService
      .getDocument(
        `${process.env.REACT_APP_SERVICE_URL}/api/report/${ticketNumber}/${licencePlate}/${selectedLanguage}/paymentSlipWithPhotos?sendViaPingen=${sendViaPingen}`
      )
      .then((response) => {
        return downloadFile(response);
      })
      .catch((e) => {
        createDocumentErrorNotification(e);
      });
  };

export const cancelTicket = (body) => async (dispatch) => {
  try {
    const res = await apiService.post(
      `${process.env.REACT_APP_SERVICE_URL}/api/report/cancelTicket`, body
    ).then(rejectIfNotOk);
    dispatch({ type: CANCEL_TICKET, payload: res.data });
    return res.data;
  } catch (e) {
    createSimpleAxiosErrorNotification(e);
    throw e;
  }
};

export const resetTicket = (ticketId, keepHolderData) => async (dispatch) => {
  try {
    const res = await apiService.put(
      `${process.env.REACT_APP_SERVICE_URL}/api/report/ticket/${ticketId}/reset`,
      { keepHolderData }
    ).then(rejectIfNotOk);

    dispatch({
      type: RESET_TICKET,
      payload: res.data,
    });

  } catch (e) {
    createSimpleAxiosErrorNotification(e);
    throw e;
  }
};

export const refundTicket = (ticketId, cancellationReason) => async (dispatch) => {
  try {
    const res = await apiService.put(
      `${process.env.REACT_APP_SERVICE_URL}/api/tickets/${ticketId}/refund`,
      { cancellationReason }
    ).then(rejectIfNotOk);
    dispatch({ type: REFUND_TICKET, payload: res.data });
    return res.data;
  } catch (e) {
    createSimpleAxiosErrorNotification(e);
    throw e;
  }
};

export const manuallyPayTicketBySysadmin =
  (manualPayment) => async (dispatch) => {
    return apiService
      .put(
        `${process.env.REACT_APP_SERVICE_URL}/api/report/manuallyPayTicketBySysadmin`,
        manualPayment
      )
      .then(rejectIfNotOk)
      .then((res) => {
        dispatch({
          type: MANUALLY_PAY,
          payload: res.data,
        });
      })
      .catch((e) => {
        createSimpleAxiosErrorNotification(e);
        throw e;
      });
  };

export const cancelTicketFee = (body) => async (dispatch) => {
  return apiService
    .post(`${process.env.REACT_APP_SERVICE_URL}/api/report/cancelTicketFee`, body)
    .then(rejectIfNotOk)
    .then((res) => {
      dispatch({
        type: CANCEL_TICKET_FEE,
        payload: res.data,
      });
    })
    .catch((e) => {
      createSimpleAxiosErrorNotification(e);
      throw e;
    });
};

export const changeTicketLegalStatusByPZAdminAndSysAdmin =
  (id, status) => async (dispatch) => {
    return apiService
      .put(
        `${process.env.REACT_APP_SERVICE_URL}/api/report/changeTicketLegalStatus/${id}/${status}`
      )
      .then(rejectIfNotOk)
      .then((res) => {
        dispatch({
          type: CHANGE_TICKET_LEGAL_STATUS,
          payload: res.data,
        });
      })
      .catch((e) => {
        createSimpleAxiosErrorNotification(e);
        throw e;
      });
  };


export const changeTicketDocumentLanguageByNumber =
    (number,language) => async (dispatch) => {
      return apiService
          .post(
              `${process.env.REACT_APP_SERVICE_URL}/api/report/editDocumentLanguage/${number}/${language}`
          )
          .then(rejectIfNotOk)
          .then((res) => {
            dispatch({
              type: CHANGE_TICKET_DOCUMENT_LANGUAGE,
              payload: res.data,
            });
          })
          .catch((e) => {
            createSimpleAxiosErrorNotification(e);
            throw e;
          });
    };

export const changeTicketArchivedStatus = (number) => async (dispatch) => {
  return apiService
    .put(`${process.env.REACT_APP_SERVICE_URL}/api/report/${number}`)
    .then(rejectIfNotOk)
    .then((res) => {
      dispatch({
        type: ARCHIVED_TICKETS,
        payload: number,
      });
    })
    .catch((e) => {
      createSimpleAxiosErrorNotification(e);
      throw e;
    });
};
export const commentTicket = (data) => async (dispatch) => {
  dispatch({
    type: SET_LOADER,
    payload: true,
  });
  return apiService
    .put(`${process.env.REACT_APP_SERVICE_URL}/api/report/commentTicket`, data)
    .then(rejectIfNotOk)
    .then((res) => {
      dispatch({
        type: COMMENT_TICKET,
        payload: { comment: data.comment, ticketNumber: data.ticketNumber },
      });
    })
    .catch((e) => {
      createSimpleAxiosErrorNotification(e);
      throw e;
    })
    .finally(() => {
      dispatch({
        type: SET_LOADER,
        payload: false,
      });
    });
};
export const modifyTicket = (data, setEditTicketModal) => async (dispatch) => {
  try {
    const res = await apiService.put(
      `${process.env.REACT_APP_SERVICE_URL}/api/report/editTicket`,
      data,
      { validateStatus: (status) => status <= 299 }
    );
    dispatch({ type: MODIFY_TICKET, payload: res.data });
    setEditTicketModal(false);
    dispatch({ type: CLEAR_TICKET_FOR_EDIT });
  } catch (e) {
    if (e.response?.status === 409) {
      return e.response.data;
    } else {
      createSimpleAxiosErrorNotification(e);
      setEditTicketModal(false);
      dispatch({ type: CLEAR_TICKET_FOR_EDIT });
    }
  }
};

export const clearOwnersForGSAndPZEdit = () => async (dispatch) => {
  dispatch({
    type: CLEAR_OWNERS_LIST_FOR_GS_AND_PZ_EDIT,
  });
};

export const getBeppolicemanParkingZones = (email) => async (dispatch) => {
  apiService
    .get(
      `${process.env.REACT_APP_SERVICE_URL}/api/parking-zone/getOwnersParkingZones/${email}`,
      email
    )
    .then((res) => {
      dispatch({
        type: OWNERS_PARKING_ZONES_FOR_EDITING_TICKET,
        payload: res.data,
      });
    })
    .catch((e) => {
      console.log(e);
    })
    .finally(() => {});
};
export const getBeppolicemanGasStationZones = (email) => async (dispatch) => {
  apiService
    .get(
      `${process.env.REACT_APP_SERVICE_URL}/api/gas-station/getOwnersGasStationZones/${email}`,
      email
    )
    .then((res) => {
      dispatch({
        type: OWNERS_GAS_STATION_ZONES_FOR_EDITING_TICKET,
        payload: res.data,
      });
    })
    .catch((e) => {
      console.log(e);
    })
    .finally(() => {});
};

export const editLockedViolatorAddress = (data) => async (dispatch) => {
  return apiService
    .put(
      `${process.env.REACT_APP_SERVICE_URL}/api/report/editViolatorAddress`,
      data
    )
    .then(rejectIfNotOk)
    .then((res) => {
      dispatch({
        type: MODIFY_TICKET,
        payload: res.data,
      });
    })
    .catch((e) => {
      createSimpleAxiosErrorNotification(e);
      throw e;
    });
};

export const getTicketForEdit = (ticketNumber) => async (dispatch) => {
  apiService
    .get(
      `${process.env.REACT_APP_SERVICE_URL}/api/report/getTicketForEdit/${ticketNumber}`
    )
    .then((res) => {
      dispatch({
        type: GET_TICKET_FOR_EDIT,
        payload: res.data,
      });
    })
    .catch((e) => {
      console.log(e);
    })
    .finally(() => {});
};

export const closeTicket = (body) => async (dispatch) => {
  return apiService
    .post(`${process.env.REACT_APP_SERVICE_URL}/api/report/closeTicket`, body)
    .then(rejectIfNotOk)
    .then((res) => {
      dispatch({
        type: CLOSE_TICKET,
        payload: res.data,
      });
    })
    .catch((e) => {
      createSimpleAxiosErrorNotification(e);
      throw e;
    });
};
